import { Component, OnInit, OnDestroy, PLATFORM_ID, inject } from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { JobService } from '../../services/job.service';
import { ToastService } from '../../services/toast.service';
import { AuthService } from '../../services/auth.service';
import { Job, JobTenantAddress } from '../../models/job.model';
import { Observable, switchMap } from 'rxjs';
import { ErrorComponent } from '../shared/error/error.component';
import { CardComponent } from '../card/card.component';
import { ButtonComponent } from '../button/button.component';
import { LanguageRoutingService } from '../../services/language-routing.service';
import { JobHighlightItemComponent } from '../shared/job-highlight-item/job-highlight-item.component';
import { getJobEmploymentTypes } from '../../../../../../libs/shared/src/lib/i18n/job-employment-types';
import { getJobWorkplaceTypes } from '../../../../../../libs/shared/src/lib/i18n/job-workplace-types';
import { MatchScoreClassPipe } from '../../../../../../libs/shared/src/lib/pipes/match-score-class.pipe';
import { MatchScoreNormalizedPipe } from '../../../../../../libs/shared/src/lib/pipes/match-score-normalized.pipe';
import { CurrencyDisplayPipe } from '../../utils/currency-display.pipe';
import { EnumLabelPipe } from '../../utils/enum-label.pipe';
import { copyToClipboard } from '../../utils/clipboard';
import { EnvironmentService } from '../../../environments/environment-loader';
import { SafeHtmlPipe } from '../../../../../../libs/shared/src/lib/pipes/safe-html.pipe';

@Component({
  selector: 'app-job-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslocoModule,
    CardComponent,
    ErrorComponent,
    ButtonComponent,
    JobHighlightItemComponent,
    MatchScoreClassPipe,
    MatchScoreNormalizedPipe,
    CurrencyDisplayPipe,
    EnumLabelPipe,
    SafeHtmlPipe,
  ],
  templateUrl: './job-detail.component.html',
  styleUrls: ['./job-detail.component.scss'],
})
export class JobDetailComponent implements OnInit, OnDestroy {
  job: Job | undefined;
  loading = true;
  error = false;
  matchScore: number | null = null;

  // Truncate state for AI job summary
  isSummaryExpanded = false;
  summaryTruncateLength = 500; // Default for desktop
  shouldTruncateSummary = false;
  truncatedSummary = '';
  fullSummary = '';

  private resizeListener: () => void = () => undefined;
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly meta = inject(Meta);
  private readonly titleService = inject(Title);
  private readonly environmentService = inject(EnvironmentService);
  private readonly structuredDataScriptId = 'job-detail-json-ld';
  private readonly canonicalLinkId = 'job-detail-canonical';

  constructor(
    private route: ActivatedRoute,
    private languageRoutingService: LanguageRoutingService,
    private router: Router,
    private jobService: JobService,
    private translocoService: TranslocoService,
    private authService: AuthService,
    private toastService: ToastService
  ) {
    // Browser-only: `window` is not available during SSR. The default
    // `summaryTruncateLength` is fine until hydration; the listener kicks
    // in after the browser takes over.
    if (isPlatformBrowser(this.platformId)) {
      this.setTruncateLengthByScreenSize();
      this.resizeListener = () => this.setTruncateLengthByScreenSize();
      window.addEventListener('resize', this.resizeListener);
    }
  }

  getLocalizedRoute(path: string): string {
    return this.languageRoutingService.getLocalizedRoute(path);
  }

  get isLoggedIn(): boolean {
    return this.authService.isLoggedIn();
  }

  get currentLanguage(): string {
    return this.translocoService.getActiveLang();
  }

  /**
   * Set truncate length based on screen size and recalculate truncation
   * Small screens show less text before truncation
   */
  private setTruncateLengthByScreenSize(): void {
    const width = window.innerWidth;

    if (width < 576) {
      // Mobile: Show less text
      this.summaryTruncateLength = 150;
    } else if (width < 768) {
      // Tablet: Medium amount
      this.summaryTruncateLength = 300;
    } else {
      // Desktop: Full default
      this.summaryTruncateLength = 500;
    }

    // Recalculate truncation when screen size changes
    this.calculateTruncation();
  }

  /**
   * Calculate truncation values - called once when data loads or screen resizes
   */
  private calculateTruncation(): void {
    const summary = this.job?.summaries && this.job.summaries.length > 0
      ? this.job.summaries.find((s) => s.lang === this.currentLanguage)?.summary || ''
      : '';

    this.fullSummary = summary;
    this.shouldTruncateSummary = summary.length > this.summaryTruncateLength;

    if (this.shouldTruncateSummary) {
      const truncated = summary.substring(0, this.summaryTruncateLength);
      // Try to truncate at last space to avoid cutting words
      const lastSpace = truncated.lastIndexOf(' ');
      const finalTruncated = lastSpace > 0 ? truncated.substring(0, lastSpace) : truncated;
      this.truncatedSummary = finalTruncated + '...';
    } else {
      this.truncatedSummary = summary;
    }
  }

  /**
   * Get the summary to display based on expanded state
   */
  get displayedSummary(): string {
    return this.isSummaryExpanded ? this.fullSummary : this.truncatedSummary;
  }

  /**
   * Toggle summary expanded state
   */
  toggleSummary(): void {
    this.isSummaryExpanded = !this.isSummaryExpanded;
  }

  ngOnInit(): void {
    // Read matchScore from query parameters (optional - from recommended jobs)
    this.route.queryParamMap.subscribe((queryParams) => {
      const scoreParam = queryParams.get('matchScore');
      if (scoreParam) {
        const parsedScore = parseFloat(scoreParam);
        // Validate score is between 0 and 1
        this.matchScore = !isNaN(parsedScore) && parsedScore >= 0 && parsedScore <= 1 ? parsedScore : null;
      } else {
        this.matchScore = null;
      }
    });

    // Load job data
    this.route.paramMap
      .pipe(
        switchMap((params) => {
          const id = params.get('id');
          if (!id) {
            this.router.navigate(['/jobs']);
            return new Observable<Job | undefined>((subscriber) => subscriber.complete());
          }

          this.loading = true;
          this.error = false;
          return this.jobService.getJobById(id);
        })
      )
      .subscribe({
        next: (job) => {
          this.job = job;
          this.loading = false;
          if (!job) {
            this.error = true;
          } else {
            // Calculate truncation after job data is loaded
            this.calculateTruncation();
            this.updateSeo(job);
          }
        },
        error: (err) => {
          console.error('Error loading job details:', err);
          this.error = true;
          this.loading = false;
        },
      });
  }

  ngOnDestroy(): void {
    // Clean up resize event listener to prevent memory leaks
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('resize', this.resizeListener);
    }
    this.removeStructuredData();
    this.removeCanonicalLink();
  }

  private updateSeo(job: Job): void {
    const title = this.buildSeoTitle(job);
    const description = this.buildMetaDescription(job);
    const canonicalUrl = this.getCanonicalUrl(job);
    const logoUrl = this.toAbsoluteUrl(job.tenantLogoUrl);

    this.titleService.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ name: 'twitter:card', content: logoUrl ? 'summary_large_image' : 'summary' });
    this.meta.updateTag({ name: 'twitter:title', content: title });
    this.meta.updateTag({ name: 'twitter:description', content: description });

    if (logoUrl) {
      this.meta.updateTag({ property: 'og:image', content: logoUrl });
      this.meta.updateTag({ name: 'twitter:image', content: logoUrl });
    } else {
      this.meta.removeTag("property='og:image'");
      this.meta.removeTag("name='twitter:image'");
    }

    this.setCanonicalLink(canonicalUrl);
    this.setStructuredData(job, canonicalUrl);
  }

  private buildSeoTitle(job: Job): string {
    const title = job.title || this.translocoService.translate('job.detail.pageTitle');
    const company = job.tenantName ? ` - ${job.tenantName}` : '';

    return `${title}${company} | Sameta Jobs`;
  }

  private buildMetaDescription(job: Job): string {
    const summary = this.normalizeText(this.fullSummary);
    const description = this.getJobDescriptionText(job);
    const salary = this.buildSalaryText(job);
    const location = this.normalizeText(this.getWorkplaceTypeDisplay(job.placeOfWork || job.location));
    const fallbackParts = [job.title, job.tenantName, location, salary].filter(Boolean);

    return (summary || description || fallbackParts.join(' - ')).slice(0, 160);
  }

  private buildSalaryText(job: Job): string {
    if (!job.salary?.basicSalary) return '';

    const upperSalary = job.salary.upperSalary ? ` - ${job.salary.upperSalary}` : '';
    const period = job.salary.period ? ` / ${job.salary.period}` : '';

    return `${job.salary.basicSalary}${upperSalary} ${this.normalizeCurrency(job.salary.currency)}${period}`.trim();
  }

  private setCanonicalLink(url: string): void {
    let link = this.document.getElementById(this.canonicalLinkId) as HTMLLinkElement | null;

    if (!link) {
      link = this.document.createElement('link');
      link.id = this.canonicalLinkId;
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }

    link.href = url;
  }

  private removeCanonicalLink(): void {
    this.document.getElementById(this.canonicalLinkId)?.remove();
  }

  private setStructuredData(job: Job, canonicalUrl: string): void {
    const structuredData = this.buildJobPostingStructuredData(job, canonicalUrl);
    let script = this.document.getElementById(this.structuredDataScriptId) as HTMLScriptElement | null;

    if (!script) {
      script = this.document.createElement('script');
      script.id = this.structuredDataScriptId;
      script.type = 'application/ld+json';
      this.document.head.appendChild(script);
    }

    script.textContent = JSON.stringify(structuredData);
  }

  private removeStructuredData(): void {
    this.document.getElementById(this.structuredDataScriptId)?.remove();
  }

  private buildJobPostingStructuredData(job: Job, canonicalUrl: string): Record<string, unknown> {
    const data: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      title: job.title,
      description: this.getJobDescriptionText(job) || this.normalizeText(this.fullSummary) || job.title,
      identifier: {
        '@type': 'PropertyValue',
        name: 'Sameta Jobs',
        value: String(job.id),
      },
      url: canonicalUrl,
      directApply: true,
      datePosted: this.toIsoDate(job.postedAt),
      hiringOrganization: {
        '@type': 'Organization',
        name: job.tenantName,
        ...(job.tenantLogoUrl ? { logo: this.toAbsoluteUrl(job.tenantLogoUrl) } : {}),
      },
    };

    const employmentTypes = this.getStructuredEmploymentTypes(job.type || job.jobType);
    const baseSalary = this.buildStructuredSalary(job);
    const jobLocations = this.buildStructuredJobLocations(job.tenantAddresses);
    const skills = job.skills?.map((skill) => skill.skillName).filter(Boolean).join(', ');
    const education = job.education && job.education !== 'Not specified' ? job.education : '';

    if (employmentTypes.length) data['employmentType'] = employmentTypes;
    if (baseSalary) data['baseSalary'] = baseSalary;
    if (jobLocations.length) data['jobLocation'] = jobLocations;
    if (job.validUntil) data['validThrough'] = this.toIsoDate(job.validUntil);
    if (job.jobCategory?.categoryKey) data['occupationalCategory'] = job.jobCategory.categoryKey;
    if (skills) data['skills'] = skills;
    if (education) data['educationRequirements'] = education;
    if (job.benefits?.length) data['jobBenefits'] = job.benefits.join(', ');
    if (this.isRemoteJob(job)) data['jobLocationType'] = 'TELECOMMUTE';

    return data;
  }

  private buildStructuredJobLocations(addresses: JobTenantAddress[] | undefined): Record<string, unknown>[] {
    return (addresses || [])
      .filter((address) => address.city || address.country || address.streetName)
      .map((address) => {
        const streetAddress = [address.streetName, address.streetNumber].filter(Boolean).join(' ');

        return {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            ...(streetAddress ? { streetAddress } : {}),
            ...(address.city ? { addressLocality: address.city } : {}),
            ...(address.zip || address.postalCode ? { postalCode: address.zip || address.postalCode } : {}),
            ...(address.country ? { addressCountry: address.country } : {}),
          },
        };
      });
  }

  private buildStructuredSalary(job: Job): Record<string, unknown> | null {
    const salary = job.salary;

    if (!salary?.basicSalary) return null;

    const value: Record<string, unknown> = {
      '@type': 'QuantitativeValue',
      unitText: this.getSalaryUnitText(salary.period),
    };

    if (salary.upperSalary && salary.upperSalary > salary.basicSalary) {
      value['minValue'] = salary.basicSalary;
      value['maxValue'] = salary.upperSalary;
    } else {
      value['value'] = salary.basicSalary;
    }

    return {
      '@type': 'MonetaryAmount',
      currency: this.normalizeCurrency(salary.currency),
      value,
    };
  }

  private getStructuredEmploymentTypes(types: string[] | undefined): string[] {
    const mapping: Record<string, string> = {
      full_time: 'FULL_TIME',
      part_time: 'PART_TIME',
      contract: 'CONTRACTOR',
      freelance: 'CONTRACTOR',
      temporary: 'TEMPORARY',
      internship: 'INTERN',
      seasonal: 'TEMPORARY',
    };

    return (types || []).map((type) => mapping[type] || type.toUpperCase()).filter(Boolean);
  }

  private getSalaryUnitText(period: string | undefined): string {
    const mapping: Record<string, string> = {
      hour: 'HOUR',
      day: 'DAY',
      week: 'WEEK',
      month: 'MONTH',
      year: 'YEAR',
    };

    return period ? mapping[period] || period.toUpperCase() : 'MONTH';
  }

  private isRemoteJob(job: Job): boolean {
    return (job.placeOfWork || job.location || []).some((place) => place.toLowerCase() === 'home');
  }

  private getCanonicalUrl(job: Job): string {
    return this.toAbsoluteUrl(this.getLocalizedRoute(`/job/${job.id}`));
  }

  private toAbsoluteUrl(url: string | undefined): string {
    if (!url) return '';

    try {
      return new URL(url, this.getOrigin()).toString();
    } catch {
      return url;
    }
  }

  private getOrigin(): string {
    const location = this.document.location;

    if (location?.origin && location.origin !== 'null') {
      return location.origin;
    }

    if (location?.protocol && location?.host) {
      return `${location.protocol}//${location.host}`;
    }

    if (this.document.baseURI) {
      try {
        return new URL(this.document.baseURI).origin;
      } catch {
        // Keep trying below.
      }
    }

    const privacyPolicyUrl = this.environmentService.environment?.legal?.privacyPolicyUrl;

    if (privacyPolicyUrl) {
      try {
        return new URL(privacyPolicyUrl).origin;
      } catch {
        return '';
      }
    }

    return '';
  }

  private toIsoDate(value: Date | string | undefined): string {
    if (!value) return new Date().toISOString();

    const date = value instanceof Date ? value : new Date(value);

    return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
  }

  private stripHtml(value: string): string {
    return value.replace(/<[^>]*>/g, ' ');
  }

  private getJobDescriptionText(job: Job): string {
    const description = this.normalizeText(this.stripHtml(job.description || ''));

    return description.toLowerCase() === 'no description provided' ? '' : description;
  }

  private normalizeText(value: string): string {
    return value.replace(/\s+/g, ' ').trim();
  }

  private normalizeCurrency(currency: string | undefined): string {
    if (!currency || currency === '€') return 'EUR';

    return currency.toUpperCase();
  }

  async copyJobLink(): Promise<void> {
    const jobLink = window.location.href;
    const ok = await copyToClipboard(jobLink);
    if (ok) {
      this.toastService.success(
        this.translocoService.translate('job.detail.copyLinkSuccess'),
        this.translocoService.translate('job.detail.copyLinkSuccessBody'),
        true,
        3000,
      );
    } else {
      this.toastService.error(
        this.translocoService.translate('job.detail.copyLinkFail'),
        jobLink,
        true,
        5000,
      );
    }
  }

  getEmploymentTypeDisplay(type: string | string[] | undefined): string {
    if (!type || (Array.isArray(type) && type.length === 0)) {
      return this.translocoService.translate('common.notSpecified');
    }

    // Get current language and employment type translations
    const currentLang = this.translocoService.getActiveLang() as 'sk' | 'en' | 'cz';
    const employmentTypes = getJobEmploymentTypes(currentLang);

    // Convert to array if string
    const typeArray = Array.isArray(type) ? type : [type];

    // Translate each type
    const translatedTypes = typeArray.map((key) => {
      const translated = employmentTypes.jobEmploymentTypes[key as keyof typeof employmentTypes.jobEmploymentTypes];
      // Fallback to formatted key if translation not found
      return (
        translated ||
        key
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      );
    });

    return translatedTypes.join(', ');
  }

  getWorkplaceTypeDisplay(placeOfWork: string | string[] | undefined): string {
    if (!placeOfWork || (Array.isArray(placeOfWork) && placeOfWork.length === 0)) {
      return this.translocoService.translate('common.notSpecified');
    }

    // Get current language and workplace type translations
    const currentLang = this.translocoService.getActiveLang() as 'sk' | 'en' | 'cz';
    const workplaceTypes = getJobWorkplaceTypes(currentLang);

    // Convert to array if string
    const placeArray = Array.isArray(placeOfWork) ? placeOfWork : [placeOfWork];

    // Translate each workplace type
    const translatedPlaces = placeArray.map((key) => {
      const translated = workplaceTypes.jobWorkplaceTypes[key.toLowerCase() as keyof typeof workplaceTypes.jobWorkplaceTypes];
      // Fallback to formatted key if translation not found
      return (
        translated ||
        key
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      );
    });

    return translatedPlaces.join(', ');
  }
}
